// --
// -- The column sets of the categorised reports, summary and drill-down.
// --
// -- Kept in one place because the three reports overlap heavily and the
// -- differences are easy to lose track of: by-vehicle adds year / make /
// -- model / part and drops Total_Price, and its drill-down shares only half
// -- its columns with the other two.
// --

export type TSummaryColumn =
  | 'category' | 'Count' | 'year' | 'make' | 'model' | 'part'
  | 'Total_Price' | 'PartPrice' | 'Shipping' | 'SalesTax' | 'vPrice' | 'FeedPrice'
  | 'Discount' | 'Gross$' | 'Gross%' | 'Avg_Price' | 'Avg_Dist' | 'Returns'
  // --
  // -- by-state's spellings of three columns the other reports already have.
  // --
  // -- Same figures underneath - avgPrice, avgDistance, discount - under the
  // -- headers that report's own query gives them. Kept apart rather than
  // -- folded into the shorter names because these headings are what people
  // -- read the report by.
  // --
  | 'Average_Price' | 'Average_Distance' | 'Total_Discount';

const MONEY_TAIL: TSummaryColumn[] = [
  'PartPrice', 'Shipping', 'SalesTax', 'vPrice', 'FeedPrice', 'Discount',
  'Gross$', 'Gross%', 'Avg_Price', 'Avg_Dist', 'Returns',
];

export const SUMMARY_BY_DATE: TSummaryColumn[] = ['category', 'Count', 'Total_Price', ...MONEY_TAIL];
export const SUMMARY_BY_VENDOR: TSummaryColumn[] = ['category', 'Count', 'Total_Price', ...MONEY_TAIL];

// -- Same columns as by-date. This report groups over the order/item join, so
// -- its Total_Price would count an order once per item - the backend prorates
// -- it by the order's item count instead, which is what makes totalling safe.
export const SUMMARY_BY_PART_TYPE: TSummaryColumn[] = ['category', 'Count', 'Total_Price', ...MONEY_TAIL];

// -- No Total_Price, only because the original report left it out. The backend
// -- prorates this one too, so it could be totalled if it were ever wanted.
export const SUMMARY_BY_VEHICLE: TSummaryColumn[] =
  ['category', 'Count', 'year', 'make', 'model', 'part', ...MONEY_TAIL];

// -- The by-date columns, grouped on status instead. An order has one status,
// -- so like by-date the groups are disjoint and nothing is prorated.
export const SUMMARY_BY_STATUS: TSummaryColumn[] = ['category', 'Count', 'Total_Price', ...MONEY_TAIL];

// -- Likewise by site. Orders with no site are left out of this one entirely,
// -- so its Count will not add up to the by-date total.
export const SUMMARY_BY_WEBSITE: TSummaryColumn[] = ['category', 'Count', 'Total_Price', ...MONEY_TAIL];

// --
// -- The narrowest summary: ten columns, in its own query's order. No shipping,
// -- sales tax or gross at all, and three columns it spells out in full.
// --
// -- Orders with no state are kept rather than dropped, so there is a "(none)"
// -- row where by-website simply omits the equivalent.
// --
export const SUMMARY_BY_STATE: TSummaryColumn[] = [
  'category', 'Count', 'Total_Price', 'PartPrice', 'Average_Price',
  'vPrice', 'FeedPrice', 'Average_Distance', 'Total_Discount', 'Returns',
];

// --
// -- by-DMA shows exactly what by-state does - the same ten columns under the
// -- same headings, grouped on the market area instead of the state.
// --
// -- Spelled out rather than aliased to SUMMARY_BY_STATE: the two agree because
// -- their source queries happen to, not by any rule that would keep them in
// -- step if one changed.
// --
export const SUMMARY_BY_DMA: TSummaryColumn[] = [
  'category', 'Count', 'Total_Price', 'PartPrice', 'Average_Price',
  'vPrice', 'FeedPrice', 'Average_Distance', 'Total_Discount', 'Returns',
];

// -- The by-date columns again, grouped on the payment code.
export const SUMMARY_BY_PAYMENT_TYPE: TSummaryColumn[] = ['category', 'Count', 'Total_Price', ...MONEY_TAIL];

// --
// -- What a stored pi_type code is called, matching the CASE in the report's
// -- own query. The summary groups on the code so the drill-down can filter on
// -- exactly that; the naming happens here.
// --
// -- A code with no name shows as itself rather than collapsing into
// -- "unknown" - otherwise several codes would share one row's label while
// -- drilling into different orders.
// --
const PAYMENT_TYPE_NAMES: Record<string, string> = {
  st: 'Stripe',
  cc: 'CreditCard',
  pp: 'Paypal',
  am: 'AmazonPay',
  af: 'Affirm',
  pt: 'PayTomorrow',
  gw: 'LKQ CCGW',
};

export const paymentTypeName = (code: string | null): string =>
  (code && PAYMENT_TYPE_NAMES[code.trim().toLowerCase()]) || code || '';

/** Columns whose values read right-aligned. */
export const SUMMARY_RIGHT: TSummaryColumn[] = [
  'Count', 'Total_Price', ...MONEY_TAIL,
  'Average_Price', 'Average_Distance', 'Total_Discount',
];

export type TDrilldownColumn = { key: string, title: string, right?: boolean };

const SHARED_MONEY: TDrilldownColumn[] = [
  { key: 'Total', title: 'Total', right: true },
  { key: 'PartPrice', title: 'PartPrice', right: true },
  { key: 'Shipping', title: 'Shipping', right: true },
  { key: 'SalesTax', title: 'SalesTax', right: true },
  { key: 'vPrice', title: 'vPrice', right: true },
  { key: 'FeedPrice', title: 'FeedPrice', right: true },
  { key: 'Discount', title: 'Discount', right: true },
  { key: 'Gross$', title: 'Gross$', right: true },
];

export const DRILLDOWN_BY_DATE: TDrilldownColumn[] = [
  { key: 'id', title: 'id' },
  { key: 'OrderID', title: 'OrderID' },
  { key: 'Status', title: 'Status' },
  { key: 'Qty', title: 'Qty' },
  ...SHARED_MONEY,
  { key: 'website', title: 'website' },
  { key: 'Name', title: 'Name' },
  { key: 'ShippedDate', title: 'Date' },
  { key: 'Distance', title: 'Distance', right: true },
];

export const DRILLDOWN_BY_VENDOR: TDrilldownColumn[] = [
  { key: 'id', title: 'id' },
  { key: 'OrderID', title: 'OrderID' },
  { key: 'Status', title: 'Status' },
  { key: 'Qty', title: 'Qty' },
  ...SHARED_MONEY,
  { key: 'Name', title: 'Name' },
  { key: 'ShippedDate', title: 'Date' },
  { key: 'Distance', title: 'Distance', right: true },
  { key: 'Coupon', title: 'Coupon' },
];

// --
// -- The same set as by-vendor, which is what the source query asks for once
// -- its repeats are dropped - it selects Discount, PartPrice, vPrice and
// -- FeedPrice twice each. Spelled out rather than aliased to by-vendor's: the
// -- two reports agree today by coincidence, not by rule.
// --
// -- No `website` column, for the obvious reason: every row has the one in the
// -- heading.
// --
export const DRILLDOWN_BY_WEBSITE: TDrilldownColumn[] = [
  { key: 'id', title: 'id' },
  { key: 'OrderID', title: 'OrderID' },
  { key: 'Status', title: 'Status' },
  { key: 'Qty', title: 'Qty' },
  ...SHARED_MONEY,
  { key: 'Name', title: 'Name' },
  { key: 'ShippedDate', title: 'Date' },
  { key: 'Distance', title: 'Distance', right: true },
  { key: 'Coupon', title: 'Coupon' },
];

// -- by-date's set plus the coupon. The only drill-down that shows both
// -- website and Coupon.
export const DRILLDOWN_BY_PART_TYPE: TDrilldownColumn[] = [
  { key: 'id', title: 'id' },
  { key: 'OrderID', title: 'OrderID' },
  { key: 'Status', title: 'Status' },
  { key: 'Qty', title: 'Qty' },
  ...SHARED_MONEY,
  { key: 'website', title: 'website' },
  { key: 'Name', title: 'Name' },
  { key: 'ShippedDate', title: 'Date' },
  { key: 'Distance', title: 'Distance', right: true },
  { key: 'Coupon', title: 'Coupon' },
];

// --
// -- No Qty, tax, shipping, discount or gross - just what the order is worth
// -- and the three per-item sums. Its `Date` is the shipping date.
// --
export const DRILLDOWN_BY_STATE: TDrilldownColumn[] = [
  { key: 'id', title: 'id' },
  { key: 'OrderID', title: 'OrderID' },
  { key: 'Status', title: 'Status' },
  { key: 'website', title: 'website' },
  { key: 'Total', title: 'Total', right: true },
  { key: 'Name', title: 'Name' },
  { key: 'ShippedDate', title: 'Date' },
  { key: 'Distance', title: 'Distance', right: true },
  { key: 'PartPrice', title: 'PartPrice', right: true },
  { key: 'vPrice', title: 'vPrice', right: true },
  { key: 'FeedPrice', title: 'FeedPrice', right: true },
];

// --
// -- The only drill-down carrying both the full money set and Last Updated.
// --
// -- Its source query is missing a comma between `website` and `Qty`, so it
// -- would not run as written; read as the two columns it plainly means.
// --
export const DRILLDOWN_BY_PAYMENT_TYPE: TDrilldownColumn[] = [
  { key: 'id', title: 'id' },
  { key: 'OrderID', title: 'OrderID' },
  { key: 'Status', title: 'Status' },
  { key: 'website', title: 'website' },
  { key: 'Qty', title: 'Qty' },
  ...SHARED_MONEY,
  { key: 'Name', title: 'Name' },
  { key: 'LastUpdated', title: 'Last Updated' },
];

// -- by-state's drill-down again, for the same reason its summary matches.
export const DRILLDOWN_BY_DMA: TDrilldownColumn[] = [
  { key: 'id', title: 'id' },
  { key: 'OrderID', title: 'OrderID' },
  { key: 'Status', title: 'Status' },
  { key: 'website', title: 'website' },
  { key: 'Total', title: 'Total', right: true },
  { key: 'Name', title: 'Name' },
  { key: 'ShippedDate', title: 'Date' },
  { key: 'Distance', title: 'Distance', right: true },
  { key: 'PartPrice', title: 'PartPrice', right: true },
  { key: 'vPrice', title: 'vPrice', right: true },
  { key: 'FeedPrice', title: 'FeedPrice', right: true },
];

// --
// -- The shortest drill-down, and the only one showing when an order last
// -- moved - which is the question a status report raises.
// --
export const DRILLDOWN_BY_STATUS: TDrilldownColumn[] = [
  { key: 'id', title: 'id' },
  { key: 'OrderID', title: 'OrderID' },
  { key: 'Status', title: 'Status' },
  { key: 'website', title: 'website' },
  { key: 'Total', title: 'Total', right: true },
  { key: 'Name', title: 'Name' },
  { key: 'LastUpdated', title: 'Last Updated' },
];

// --
// -- Note this report's `Date` is the order's creation time, where the others
// -- mean the shipping date. Same header, different column - hence the
// -- distinct keys.
// --
export const DRILLDOWN_BY_VEHICLE: TDrilldownColumn[] = [
  { key: 'id', title: 'id' },
  { key: 'OrderID', title: 'OrderID' },
  { key: 'CreatedDate', title: 'Date' },
  { key: 'website', title: 'website' },
  { key: 'Year', title: 'Year' },
  { key: 'PartType', title: 'PartType' },
  { key: 'Status', title: 'Status' },
  { key: 'Total', title: 'Total', right: true },
  { key: 'Stock#', title: 'Stock#' },
];
